import { create } from 'zustand';
import { activityService, Activity, CreateActivityRequest, UpdateActivityRequest, ActivityQueryParams } from '@/services/activityService';
import { toast } from 'sonner';

interface ActivityState {
  activities: Activity[];
  currentActivity: Activity | null;
  loading: boolean;
  error: string | null;
  
  // Pagination
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    totalActivities?: number;
  } | null;

  // Actions
  fetchActivities: (params?: ActivityQueryParams) => Promise<void>;
  fetchActivity: (id: string) => Promise<void>;
  createActivity: (data: CreateActivityRequest) => Promise<void>;
  updateActivity: (id: string, data: UpdateActivityRequest) => Promise<void>;
  deleteActivity: (id: string) => Promise<void>;
  updateActivityStatus: (id: string, status: Activity['status']) => Promise<void>;
  completeActivity: (id: string) => Promise<void>;
  fetchActivitiesByPractice: (practiceId: string) => Promise<Activity[]>;
  fetchActivitiesByUser: (userId: string, params?: ActivityQueryParams) => Promise<Activity[]>;
  bulkAssign: (activityIds: string[], userId: string) => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

function mergeActivity(existing: Activity, incoming: Activity): Activity {
  return {
    ...existing,
    ...incoming,
    practice: existing.practice && incoming.practice
      ? { ...existing.practice, ...incoming.practice }
      : incoming.practice ?? existing.practice,
  } as Activity;
}

const initialState = {
  activities: [],
  currentActivity: null,
  loading: false,
  error: null,
  pagination: null,
};

export const useActivityStore = create<ActivityState>((set, get) => ({
  ...initialState,

  fetchActivities: async (params?: ActivityQueryParams) => {
    set({ loading: true, error: null });
    try {
      const response = await activityService.getActivities(params);
      set({
        activities: response.data,
        pagination: response.pagination || null,
        loading: false,
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Errore nel caricamento delle attività';
      set({
        error: errorMessage,
        loading: false,
      });
      toast.error(errorMessage);
    }
  },

  fetchActivity: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const response = await activityService.getActivity(id);
      set({
        currentActivity: response.data,
        loading: false,
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Errore nel caricamento dell\'attività';
      set({
        error: errorMessage,
        loading: false,
      });
      toast.error(errorMessage);
    }
  },

  createActivity: async (data: CreateActivityRequest) => {
    set({ loading: true, error: null });
    try {
      const response = await activityService.createActivity(data);
      set((state) => ({
        activities: [response.data, ...state.activities],
        loading: false,
      }));
      toast.success(response.message || 'Attività creata con successo');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Errore nella creazione dell\'attività';
      set({
        error: errorMessage,
        loading: false,
      });
      toast.error(errorMessage);
      throw error;
    }
  },

  updateActivity: async (id: string, data: UpdateActivityRequest) => {
    set({ loading: true, error: null });
    try {
      const response = await activityService.updateActivity(id, data);
      set((state) => ({
        activities: state.activities.map(a => a.id === id ? mergeActivity(a, response.data) : a),
        currentActivity: state.currentActivity?.id === id ? mergeActivity(state.currentActivity, response.data) : state.currentActivity,
        loading: false,
      }));
      toast.success(response.message || 'Attività aggiornata con successo');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Errore nell\'aggiornamento dell\'attività';
      set({
        error: errorMessage,
        loading: false,
      });
      toast.error(errorMessage);
      throw error;
    }
  },

  deleteActivity: async (id: string) => {
    set({ loading: true, error: null });
    try {
      await activityService.deleteActivity(id);
      set((state) => ({
        activities: state.activities.filter(a => a.id !== id),
        currentActivity: state.currentActivity?.id === id ? null : state.currentActivity,
        loading: false,
      }));
      toast.success('Attività eliminata con successo');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Errore nell\'eliminazione dell\'attività';
      set({
        error: errorMessage,
        loading: false,
      });
      toast.error(errorMessage);
      throw error;
    }
  },

  updateActivityStatus: async (id: string, status: Activity['status']) => {
    set({ loading: true, error: null });
    try {
      const response = await activityService.updateActivityStatus(id, status);
      set((state) => ({
        activities: state.activities.map(a => a.id === id ? mergeActivity(a, response.data) : a),
        currentActivity: state.currentActivity?.id === id ? mergeActivity(state.currentActivity, response.data) : state.currentActivity,
        loading: false,
      }));
      toast.success(response.message || 'Stato attività aggiornato con successo');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Errore nell\'aggiornamento dello stato';
      set({
        error: errorMessage,
        loading: false,
      });
      toast.error(errorMessage);
      throw error;
    }
  },

  completeActivity: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const response = await activityService.completeActivity(id);
      set((state) => ({
        activities: state.activities.map(a => a.id === id ? mergeActivity(a, response.data) : a),
        currentActivity: state.currentActivity?.id === id ? mergeActivity(state.currentActivity, response.data) : state.currentActivity,
        loading: false,
      }));
      toast.success(response.message || 'Attività completata con successo');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Errore nel completamento dell\'attività';
      set({
        error: errorMessage,
        loading: false,
      });
      toast.error(errorMessage);
      throw error;
    }
  },

  fetchActivitiesByPractice: async (practiceId: string): Promise<Activity[]> => {
    set({ loading: true, error: null });
    try {
      const response = await activityService.getActivitiesByPractice(practiceId);
      set({ loading: false });
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Errore nel caricamento delle attività';
      set({
        error: errorMessage,
        loading: false,
      });
      toast.error(errorMessage);
      return [];
    }
  },

  fetchActivitiesByUser: async (userId: string, params?: ActivityQueryParams): Promise<Activity[]> => {
    set({ loading: true, error: null });
    try {
      const response = await activityService.getActivitiesByUser(userId, params);
      set({ loading: false });
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Errore nel caricamento delle attività';
      set({
        error: errorMessage,
        loading: false,
      });
      toast.error(errorMessage);
      return [];
    }
  },

  bulkAssign: async (activityIds: string[], userId: string) => {
    set({ loading: true, error: null });
    try {
      const response = await activityService.bulkAssign(activityIds, userId);
      set({ loading: false });
      toast.success(response.message || 'Attività assegnate con successo');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Errore nell\'assegnazione massiva';
      set({
        error: errorMessage,
        loading: false,
      });
      toast.error(errorMessage);
      throw error;
    }
  },

  clearError: () => {
    set({ error: null });
  },

  reset: () => {
    set(initialState);
  },
}));


